<div class="space-y-6 pb-10">
  <div class="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
    <div class="flex flex-col gap-4 md:flex-row md:items-center">
      <div>
        <h2 class="text-3xl font-black tracking-tight">Orders Center</h2>
        <div class="mt-1 flex items-center gap-2">
          <span class="h-2 w-2 rounded-full bg-success animate-pulse"></span>
          <p class="text-xs font-bold opacity-40 uppercase tracking-widest">Live store orders</p>
        </div>
      </div>

      @if (!isStoreManager()) {
        <div class="join w-fit rounded-2xl border border-base-300 bg-base-200/50 p-1">
          <div class="join-item flex flex-col justify-center border-r border-base-300 px-4">
            <span class="text-[8px] font-black opacity-30 uppercase tracking-[0.2em]">Fulfilling From</span>
            <span class="text-[10px] font-black text-primary uppercase">Active Store</span>
          </div>
          <select
            class="select select-sm join-item min-w-[220px] border-none bg-transparent text-xs font-black uppercase tracking-wider focus:outline-none"
            [ngModel]="selectedStoreId()"
            (ngModelChange)="onStoreChange($event)">
            <option [ngValue]="null">All stores</option>
            @for (store of stores(); track store.id) {
              <option [ngValue]="store.id.toString()">{{ store.name }} ({{ store.city }})</option>
            }
          </select>
        </div>
      }
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <a class="btn btn-sm btn-outline rounded-xl" routerLink="/operations/picking">Picking queue</a>
      <button class="btn btn-sm btn-primary bg-primary/10 border-primary/20 rounded-xl font-bold text-primary" routerLink="/orders/assisted-new">
        <i class="fa-solid fa-plus text-[10px]"></i> Create Assisted Order
      </button>

      <select class="select select-sm select-bordered rounded-xl bg-base-100 text-xs font-bold" [(ngModel)]="paymentStatusFilter" (ngModelChange)="currentPage = 1; loadOrders()">
        <option value="ALL">ALL PAYMENTS</option>
        <option value="PENDING">PENDING</option>
        <option value="PAID">PAID</option>
        <option value="FAILED">FAILED</option>
      </select>

      <div class="flex items-center gap-2 rounded-xl border border-base-300 bg-base-200/50 px-3 py-1">
        <input type="date" class="input input-xs border-none bg-transparent text-[10px] font-bold focus:outline-none" [ngModel]="fromDate()" (ngModelChange)="fromDate.set($event); currentPage = 1; loadOrders()">
        <span class="text-[10px] font-black opacity-20">TO</span>
        <input type="date" class="input input-xs border-none bg-transparent text-[10px] font-bold focus:outline-none" [ngModel]="toDate()" (ngModelChange)="toDate.set($event); currentPage = 1; loadOrders()">
      </div>

      <div class="relative">
        <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-base-content/40"></i>
        <input
          type="text"
          placeholder="Search order..."
          class="input input-sm input-bordered w-44 rounded-xl pl-9 text-xs font-bold"
          [(ngModel)]="searchTerm"
          (keyup.enter)="currentPage = 1; loadOrders()">
      </div>

      <button class="btn btn-sm btn-primary rounded-xl" (click)="loadOrders()" [disabled]="isLoading()">
        <i class="fa-solid fa-rotate" [class.animate-spin]="isLoading()"></i>
      </button>
    </div>
  </div>

  <div class="flex flex-wrap gap-2">
    @for (tab of statusTabs; track tab) {
      <button
        class="btn btn-sm rounded-xl border-base-300 text-xs"
        [class.btn-primary]="activeStatusTab === tab"
        [class.btn-outline]="activeStatusTab !== tab"
        (click)="changeStatusTab(tab)">
        {{ tab | titlecase }}
      </button>
    }
  </div>

  <div class="overflow-hidden rounded-2xl border border-base-200 bg-base-100 shadow-xl shadow-base-200/50">
    <div class="overflow-x-auto">
      <table class="table table-md w-full">
        <thead class="bg-base-200/40 text-[10px] font-black uppercase tracking-[0.2em] text-base-content/40">
          <tr>
            <th class="pl-6 py-5">Order</th>
            <th>Customer</th>
            <th>Delivery</th>
            <th>Items</th>
            <th>Amount</th>
            <th>Status</th>
            <th class="pr-6 text-right">Action</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-base-200">
          @if (isLoading()) {
            @for (i of [1, 2, 3, 4]; track i) {
              <tr class="animate-pulse">
                <td colspan="7" class="h-20 px-6">
                  <div class="flex items-center gap-5">
                    <div class="h-10 w-36 rounded-xl bg-base-200"></div>
                    <div class="h-10 w-56 rounded-xl bg-base-200"></div>
                    <div class="ml-auto h-10 w-28 rounded-xl bg-base-200"></div>
                  </div>
                </td>
              </tr>
            }
          } @else {
            @for (order of orders(); track order.order_id) {
              <tr class="group transition hover:bg-base-200/30">
                <td class="pl-6 py-5">
                  <div class="flex flex-col gap-1 items-start">
                    <div class="flex items-center gap-2">
                       <span class="text-sm font-black">#{{ order.order_number || order.order_id }}</span>
                       @if (order.order_source === 'ADMIN_ASSISTED') {
                          <span class="badge badge-primary font-black border-none text-[8px] uppercase tracking-widest px-1.5 py-0 h-4 min-h-0">Assisted</span>
                       }
                    </div>
                    <span class="text-[10px] font-bold uppercase tracking-widest opacity-40">{{ order.created_at | istDate:'MMM d, y, h:mm a' }}</span>
                    <span class="text-[10px] font-bold opacity-50">{{ order.store?.name || order.store?.store_name || 'Store' }}</span>
                  </div>
                </td>

                <td>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-black">{{ order.customer.receiver_name || order.customer.name || 'Customer' }}</span>
                    <span class="text-[11px] font-mono opacity-60">{{ order.delivery_phone || order.customer.phone }}</span>
                  </div>
                </td>

                <td>
                  <div class="max-w-[260px]">
                    <p class="truncate text-[11px] font-bold leading-relaxed opacity-70" [class.text-error]="order.address_status !== 'Valid'">
                      {{ order.delivery_address }}
                    </p>
                    @if (order.delivery?.address?.receiver_name) {
                      <p class="mt-1 text-[10px] font-black uppercase tracking-widest opacity-35">Receiver: {{ order.delivery?.address?.receiver_name }}</p>
                    }
                  </div>
                </td>

                <td>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs font-black">{{ order.summary.items }} lines / {{ order.summary.quantity }} qty</span>
                    <span class="max-w-[220px] truncate text-[10px] font-bold opacity-50">{{ order.items[0]?.product_name || 'No items' }}</span>
                  </div>
                </td>

                <td>
                  <div class="flex flex-col gap-1">
                    <span class="text-sm font-black text-primary">Rs {{ order.payable_amount | number:'1.0-0' }}</span>
                    <span class="badge badge-xs border-0 text-[9px] font-black uppercase" [ngClass]="getPaymentClass(order.payment_status)">
                      {{ order.payment_status || 'PENDING' }}
                    </span>
                  </div>
                </td>

                <td>
                  <div class="flex flex-col gap-1.5">
                    <span class="badge badge-sm border text-[9px] font-black tracking-wider" [ngClass]="getStatusClass(order.current_status)">
                      {{ order.current_status }}
                    </span>
                    <span class="text-[9px] font-black uppercase tracking-[0.12em]" [ngClass]="{
                      'text-error': order.priority === 'HIGH',
                      'text-warning': order.priority === 'MEDIUM',
                      'text-success': order.priority === 'LOW'
                    }">
                      {{ order.priority }} priority
                    </span>
                  </div>
                </td>

                <td class="pr-6 text-right">
                  <div class="flex items-center justify-end gap-2">
                    @if (order.current_status === 'PENDING') {
                      <button class="btn btn-xs btn-primary rounded-lg" (click)="updateStatus(order.order_id, 'CONFIRMED')">Confirm</button>
                    }
                    @if (order.current_status === 'CONFIRMED') {
                      <button class="btn btn-xs btn-secondary rounded-lg" (click)="updateStatus(order.order_id, 'PREPARING')">Prepare</button>
                    }
                    @if (order.current_status === 'PREPARING') {
                      <button class="btn btn-xs btn-accent rounded-lg text-white" (click)="updateStatus(order.order_id, 'READY_FOR_PICKUP', order)">Ready & Assign</button>
                    }
                    @if (order.current_status === 'RIDER_AT_STORE') {
                      <button class="btn btn-xs btn-info rounded-lg text-white" (click)="updateStatus(order.order_id, 'DISPATCHED')">Confirm Handover</button>
                    }
                    @if (canManualAssignRider(order)) {
                      <button class="btn btn-xs btn-warning rounded-lg" (click)="openManualAssignment(order)">
                        {{ order.current_status === 'READY_FOR_PICKUP' ? 'Assign rider' : 'Pack & assign rider' }}
                      </button>
                    }
                    @if (order.current_status === 'DISPATCHED' || order.current_status === 'RIDER_NEAR_CUSTOMER') {
                      <button class="btn btn-xs btn-success rounded-lg text-white" (click)="updateStatus(order.order_id, 'DELIVERED')">Deliver</button>
                    }
                    <button class="btn btn-xs btn-outline rounded-lg" (click)="showOrder(order)">
                      <i class="fa-solid fa-eye"></i>
                      Show
                    </button>
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    @if (!isLoading() && orders().length === 0) {
      <div class="flex flex-col items-center justify-center bg-base-100/50 py-24">
        <div class="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-base-300 bg-base-200">
          <i class="fa-solid fa-box-open text-3xl opacity-20"></i>
        </div>
        <h3 class="text-xl font-black opacity-60">No matching orders found</h3>
        <p class="mt-2 text-xs font-bold uppercase tracking-widest opacity-30">Adjust filters to see orders</p>
      </div>
    }
  </div>

  <div class="flex flex-col items-center justify-between gap-4 px-2 md:flex-row">
    <div class="flex items-center gap-4">
      <span class="text-[10px] font-black uppercase tracking-[0.2em] opacity-30">
        Displaying {{ orders().length }} of {{ totalOrders() | number }} entries
      </span>
      <select class="select select-xs select-bordered rounded-lg text-[10px] font-black" [(ngModel)]="pageSize" (ngModelChange)="currentPage = 1; loadOrders()">
        <option [ngValue]="10">10 PER PAGE</option>
        <option [ngValue]="25">25 PER PAGE</option>
        <option [ngValue]="50">50 PER PAGE</option>
      </select>
    </div>

    <div class="flex items-center gap-2">
      <button class="btn btn-sm btn-outline rounded-xl border-base-300 px-4" (click)="prevPage()" [disabled]="currentPage === 1">
        <i class="fa-solid fa-chevron-left text-[10px]"></i>
      </button>
      <div class="rounded-xl border border-base-300 bg-base-200/50 px-4 py-2 text-[10px] font-black">
        PAGE {{ currentPage }}
      </div>
      <button class="btn btn-sm btn-outline rounded-xl border-base-300 px-4" (click)="nextPage()" [disabled]="orders().length < pageSize">
        <i class="fa-solid fa-chevron-right text-[10px]"></i>
      </button>
    </div>
  </div>

  @if (selectedOrder(); as selected) {
    <div class="fixed inset-0 z-50 flex items-end justify-end bg-black/40 p-0 backdrop-blur-sm md:p-6">
      <button type="button" class="absolute inset-0 cursor-default" aria-label="Close order details" (click)="closeOrderDetails()"></button>
      <div class="relative flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-3xl border border-base-300 bg-base-100 shadow-2xl md:rounded-3xl">
        <div class="flex flex-col gap-4 border-b border-base-200 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p class="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Order Details</p>
            <h3 class="text-2xl font-black">#{{ selected.order_number || selected.order_id }}</h3>
            <p class="text-xs font-bold opacity-50">{{ selected.created_at | istDate:'full' }}</p>
          </div>
          <div class="flex items-center gap-2">
            <span class="badge badge-sm border font-black" [ngClass]="getStatusClass(selected.current_status)">{{ selected.current_status }}</span>
            <span class="badge badge-sm border-0 font-black uppercase" [ngClass]="getPaymentClass(selected.payment_status)">{{ selected.payment_status }}</span>
            <button (click)="openOrderDetailsFull(selected.order_id)" class="btn btn-sm btn-primary btn-outline ml-2 rounded-xl">Get full detail</button>
            <button class="btn btn-sm btn-ghost btn-circle" (click)="closeOrderDetails()">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        @if (isLoadingOrderDetails()) {
        <div class="h-1 w-full overflow-hidden bg-primary/10">
          <div class="h-full w-1/3 animate-pulse rounded-full bg-primary"></div>
        </div>
        }

        <div class="flex-1 overflow-y-auto p-5">
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div class="space-y-6 lg:col-span-2">
              @if (['CONFIRMED','PREPARING','READY_FOR_PICKUP','RIDER_ASSIGNED','RIDER_ACCEPTED','RIDER_AT_STORE'].includes(selected.current_status)) {
                <app-packing-task [orderId]="selected.order_id" (changed)="showOrder(selected)" />
              }
              <div class="rounded-2xl border border-base-200 bg-base-100">
                <div class="flex items-center justify-between border-b border-base-200 px-5 py-4">
                  <h4 class="text-sm font-black uppercase tracking-widest opacity-60">Items</h4>
                  <span class="badge badge-sm font-black border-base-300 px-3 text-[10px]">Original Lines: {{ selected.summary?.items || 0 }} | Active Lines: {{ getActiveLinesCount(selected) }} | Fulfilled Qty: {{ getFulfilledQty(selected) }}</span>
                </div>
                <div class="divide-y divide-base-200">
                  @for (item of selected.items; track (item?.variant_sku || item?.sku || '') + $index) {
                      <div class="flex items-start gap-4 p-5 hover:bg-base-200/5 transition" [class.opacity-40]="item.is_fully_removed">
                        <div class="h-14 w-14 shrink-0 bg-base-200 rounded-2xl flex items-center justify-center border border-base-300 relative overflow-hidden">
                           @if (item.image_url) {
                              <img [src]="item.image_url" class="absolute inset-0 h-full w-full object-cover">
                           } @else {
                              <i class="fa-solid fa-box text-base-content/20 text-xl"></i>
                           }
                           @if (item.is_fully_removed) {
                              <div class="absolute inset-0 bg-base-100/70 border-2 border-error text-error text-[10px] font-black h-full w-full flex flex-col items-center justify-center text-center leading-none">
                                  <span>REMOVED</span>
                              </div>
                           }
                        </div>
                        <div class="flex-1 min-w-0">
                           <p class="text-sm font-black leading-tight" [class.line-through]="item.is_fully_removed">
                              {{ item.product_name }}
                           </p>
                           <div class="flex flex-wrap gap-2 mt-2">
                              <span class="text-[10px] font-mono bg-base-200 px-2 py-0.5 rounded opacity-60">SKU: {{ item.variant_sku || item.sku }}</span>
                              <span class="text-[10px] bg-primary/10 text-primary font-bold px-2 py-0.5 rounded uppercase">{{ item.variant_name }}</span>
                              <span class="text-[10px] bg-base-200 font-bold px-2 py-0.5 rounded uppercase">Ordered {{ item.ordered_quantity }}</span>
                              @if (item.is_partially_removed || item.is_fully_removed) {
                                  <span class="text-[10px] font-bold px-2 py-0.5 rounded uppercase" [ngClass]="item.is_fully_removed ? 'bg-error/10 text-error' : 'bg-warning/10 text-warning'">Removed {{ item.removed_quantity }} | Fulfilled {{ item.fulfilled_quantity }}</span>
                              }
                           </div>
                        </div>
                        <div class="text-right">
                           <p class="text-xs opacity-40 font-bold line-through">Rs {{ item.original_line_total | number:'1.0-0' }}</p>
                           <p class="text-sm font-black mt-1" [class.text-error]="item.is_fully_removed" [class.text-primary]="!item.is_fully_removed">Rs {{ item.fulfilled_line_total | number:'1.0-0' }}</p>
                        </div>
                     </div>
                  }
                </div>
              </div>

              <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
                <div class="mb-5 flex items-center justify-between gap-3">
                  <h4 class="text-sm font-black uppercase tracking-widest opacity-60">Assigned Rider</h4>
                  @if (selected.rider?.presence) {
                  <span
                    class="badge badge-sm border-0 font-black uppercase"
                    [class.badge-success]="selected.rider?.presence?.is_online"
                    [class.badge-ghost]="!selected.rider?.presence?.is_online"
                  >
                    {{ selected.rider?.presence?.is_online ? 'Online' : 'Offline' }}
                  </span>
                  }
                </div>

                @if (selected.rider; as rider) {
                <div class="space-y-5">
                  <div class="flex items-center gap-4">
                    <div class="h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-base-300 bg-primary/10">
                      @if (rider.profile_photo_url) {
                      <img
                        [src]="rider.profile_photo_url"
                        (error)="rider.profile_photo_url = null"
                        [alt]="rider.full_name || rider.name || 'Assigned rider'"
                        class="h-full w-full object-cover"
                      >
                      } @else {
                      <div class="flex h-full w-full items-center justify-center text-xl font-black text-primary">
                        {{ (rider.full_name || rider.name || 'R')[0] }}
                      </div>
                      }
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="truncate text-base font-black">{{ rider.full_name || rider.name || 'Rider' }}</p>
                      <p class="text-xs font-bold opacity-50">{{ rider.code || 'No rider code' }}</p>
                      <div class="mt-2 flex flex-wrap gap-2">
                        <span class="badge badge-sm badge-outline uppercase">{{ rider.vehicle_type || 'Vehicle not set' }}</span>
                        <span class="badge badge-sm badge-outline uppercase">{{ rider.status || 'Unknown status' }}</span>
                        <span
                          class="badge badge-sm border-0 uppercase"
                          [class.badge-success]="rider.verification_status?.toLowerCase() === 'approved'"
                          [class.badge-warning]="rider.verification_status?.toLowerCase() !== 'approved'"
                        >
                          {{ rider.verification_status || 'Not verified' }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Phone</p>
                      @if (rider.account?.phone) {
                      <a class="mt-1 block font-mono font-black text-primary" [href]="'tel:' + rider.account?.phone">
                        {{ rider.account?.phone }}
                      </a>
                      } @else {
                      <p class="mt-1 font-bold opacity-50">Not available</p>
                      }
                    </div>
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Email</p>
                      <p class="mt-1 break-all font-bold">{{ rider.account?.email || 'Not available' }}</p>
                    </div>
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Rider ID</p>
                      <p class="mt-1 break-all font-mono text-[10px] font-bold">{{ rider.id }}</p>
                    </div>
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">User ID</p>
                      <p class="mt-1 break-all font-mono text-[10px] font-bold">{{ rider.user_id || '-' }}</p>
                    </div>
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Presence</p>
                      <p class="mt-1 font-black uppercase">{{ rider.presence?.status || 'Unknown' }}</p>
                    </div>
                    <div class="rounded-xl bg-base-200/60 p-3">
                      <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Last seen</p>
                      <p class="mt-1 font-bold">{{ rider.presence?.last_seen_at ? (rider.presence?.last_seen_at | istDate:'short') : 'Not available' }}</p>
                    </div>
                  </div>

                  @if (rider.presence?.latitude !== null && rider.presence?.latitude !== undefined && rider.presence?.longitude !== null && rider.presence?.longitude !== undefined) {
                  <div class="rounded-xl border border-info/20 bg-info/5 p-3">
                    <p class="text-[9px] font-black uppercase tracking-widest text-info">Latest rider location</p>
                    <p class="mt-1 font-mono text-[10px] font-bold">
                      {{ rider.presence?.latitude }}, {{ rider.presence?.longitude }}
                    </p>
                  </div>
                  }

                  <div class="space-y-2 border-t border-base-200 pt-4 text-xs">
                    <div class="flex justify-between gap-3">
                      <span class="opacity-50">Assigned</span>
                      <span class="text-right font-bold">{{ selected.assigned_at ? (selected.assigned_at | istDate:'short') : 'Not recorded' }}</span>
                    </div>
                    @if (selected.deliveries?.[0]; as delivery) {
                    <div class="flex justify-between gap-3">
                      <span class="opacity-50">Delivery status</span>
                      <span class="font-black uppercase">{{ delivery.status || '-' }}</span>
                    </div>
                    <div class="flex justify-between gap-3">
                      <span class="opacity-50">Picked up</span>
                      <span class="text-right font-bold">{{ delivery.picked_at ? (delivery.picked_at | istDate:'short') : 'Not yet' }}</span>
                    </div>
                    <div class="flex justify-between gap-3">
                      <span class="opacity-50">Distance / ETA</span>
                      <span class="text-right font-bold">
                        {{ delivery.estimated_distance_km ?? '-' }} km / {{ delivery.estimated_delivery_minutes ?? '-' }} min
                      </span>
                    </div>
                    <div class="flex justify-between gap-3">
                      <span class="opacity-50">Rider payout</span>
                      <span class="font-bold">Rs {{ (delivery.final_payout ?? delivery.estimated_payout ?? 0) | number:'1.0-0' }}</span>
                    </div>
                    }
                  </div>
                </div>
                } @else if (selected.rider_id) {
                <div class="rounded-xl border border-warning/20 bg-warning/5 p-4">
                  <p class="text-sm font-black">Rider ID: {{ selected.rider_id }}</p>
                  <p class="mt-1 text-xs opacity-60">The rider record could not be loaded.</p>
                </div>
                } @else {
                <div class="rounded-xl border-2 border-dashed border-base-200 p-5 text-center">
                  <i class="fa-solid fa-motorcycle text-2xl opacity-25"></i>
                  <p class="mt-2 text-xs font-black uppercase opacity-40">No rider assigned</p>
                </div>
                }
              </div>

              <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
                <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Status History</h4>
                <div class="space-y-5">
                  @for (history of selected.status_history; track history.created_at + history.status) {
                    <div class="flex items-start gap-4">
                      <div class="mt-1 h-3 w-3 rounded-full bg-primary ring-4 ring-primary/10"></div>
                      <div class="flex-1">
                        <p class="text-xs font-black uppercase tracking-widest text-primary">{{ history.status }}</p>
                        <p class="mt-1 text-[11px] font-bold opacity-50">{{ history.note || 'Status updated' }} {{ history.actor ? 'by ' + history.actor : '' }}</p>
                      </div>
                      <span class="text-[10px] font-mono opacity-40">{{ history.created_at | istDate:'short' }}</span>
                    </div>
                  }
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
                <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Customer & Delivery</h4>
                <div class="space-y-4">
                  <div>
                    <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Customer</p>
                    <p class="text-sm font-black">{{ selected.customer.receiver_name || selected.customer.name || 'Customer' }}</p>
                    <p class="text-xs font-mono opacity-60">{{ selected.customer.phone || selected.delivery_phone }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Receiver</p>
                    <p class="text-sm font-black">{{ selected.delivery?.address?.receiver_name || selected.customer.receiver_name || '-' }}</p>
                    <p class="text-xs font-mono opacity-60">{{ selected.delivery?.phone || selected.delivery?.address?.phone_number || selected.delivery_phone }}</p>
                  </div>
                  <div>
                    <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Address</p>
                    <p class="mt-1 text-xs font-bold leading-relaxed">{{ selected.delivery_address }}</p>
                    @if (selected.delivery?.lat && selected.delivery?.lng) {
                      <p class="mt-2 text-[10px] font-mono opacity-45">Lat {{ selected.delivery?.lat }}, Lng {{ selected.delivery?.lng }}</p>
                    }
                  </div>
                </div>
              </div>

              <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
                <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Pricing</h4>
                @if (selected.canonical_bill) {
                   <app-order-bill-summary [canonicalBill]="selected.canonical_bill" mode="compact"></app-order-bill-summary>
                } @else {
                   <div class="p-4 border-2 border-dashed border-base-200 rounded-xl text-center">
                      <p class="text-[10px] font-black uppercase text-base-content/40 tracking-widest">Legacy bill</p>
                      <p class="font-black text-xl mt-1">Rs {{ selected.payable_amount | number:'1.0-0' }}</p>
                   </div>
                }
              </div>

              @if ((selected.promotions || []).length > 0) {
                <div class="rounded-2xl border border-success/20 bg-success/5 p-5">
                  <h4 class="mb-4 text-sm font-black uppercase tracking-widest text-success">Promotions</h4>
                  <div class="space-y-3">
                    @for (promotion of selected.promotions; track promotion.promotion_id || promotion.name) {
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <p class="text-xs font-black">{{ promotion.name }}</p>
                          <p class="text-[10px] font-bold uppercase opacity-45">{{ promotion.type }}</p>
                        </div>
                        <span class="text-xs font-black text-success">Rs {{ promotion.discount_amount || 0 }}</span>
                      </div>
                    }
                  </div>
                </div>
              }

              <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
                <h4 class="mb-4 text-sm font-black uppercase tracking-widest opacity-60">Next Step</h4>
                <p class="mb-4 text-xs font-bold opacity-60">{{ selected.action_required }}</p>
                <div class="flex flex-wrap gap-2">
                  @if (selected.current_status === 'PENDING') {
                    <button class="btn btn-sm btn-primary rounded-xl" (click)="updateStatus(selected.order_id, 'CONFIRMED')">Confirm</button>
                  }
                  @if (selected.current_status === 'CONFIRMED') {
                    <button class="btn btn-sm btn-secondary rounded-xl" (click)="updateStatus(selected.order_id, 'PREPARING')">Prepare</button>
                  }
                  @if (selected.current_status === 'PREPARING') {
                    <button class="btn btn-sm btn-accent rounded-xl text-white" (click)="updateStatus(selected.order_id, 'READY_FOR_PICKUP', selected)">Ready & Assign</button>
                  }
                  @if (canManualAssignRider(selected)) {
                    <button class="btn btn-sm btn-warning rounded-xl" (click)="openManualAssignment(selected)">{{ selected.current_status === 'READY_FOR_PICKUP' ? 'Assign rider' : 'Pack & assign rider' }}</button>
                  }
                  @if (selected.current_status === 'RIDER_AT_STORE') {
                    <button class="btn btn-sm btn-info rounded-xl text-white" (click)="updateStatus(selected.order_id, 'DISPATCHED')">Confirm Handover</button>
                  }
                  @if (selected.current_status === 'DISPATCHED' || selected.current_status === 'RIDER_NEAR_CUSTOMER') {
                    <button class="btn btn-sm btn-success rounded-xl text-white" (click)="updateStatus(selected.order_id, 'DELIVERED')">Deliver</button>
                  }
                  @if (selected.current_status !== 'CANCELLED' && selected.current_status !== 'DELIVERED') {
                    <button class="btn btn-sm btn-outline btn-error rounded-xl" (click)="updateStatus(selected.order_id, 'CANCELLED')">Cancel</button>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  @if (manualAssignmentOrder(); as manualOrder) {
    <div class="fixed inset-0 z-[60] flex items-end justify-end bg-black/40 p-0 backdrop-blur-sm md:p-6">
      <button type="button" class="absolute inset-0 cursor-default" aria-label="Close rider assignment" (click)="closeManualAssignment()"></button>
      <div class="relative flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl border border-base-300 bg-base-100 shadow-2xl md:rounded-3xl">
        <div class="flex items-center justify-between border-b border-base-200 p-5">
          <div>
            <p class="text-[10px] font-black uppercase tracking-[0.2em] text-warning">Manual Rider Assignment</p>
            <h3 class="text-2xl font-black">#{{ manualOrder.order_number || manualOrder.order_id }}</h3>
            <p class="text-xs font-bold opacity-50">Choose an approved rider for this store</p>
          </div>
          <button class="btn btn-sm btn-ghost btn-circle" (click)="closeManualAssignment()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="grid gap-5 overflow-y-auto p-5 md:grid-cols-[1fr_1.4fr]">
          <div class="rounded-2xl border border-base-200 bg-base-100 p-5">
            <h4 class="mb-4 text-sm font-black uppercase tracking-widest opacity-60">Order</h4>
            <div class="space-y-4 text-xs">
              <div>
                <p class="font-black uppercase tracking-widest opacity-35">Store</p>
                <p class="mt-1 font-bold">{{ manualOrder.store?.name || manualOrder.store?.store_name || 'Store' }}</p>
              </div>
              <div>
                <p class="font-black uppercase tracking-widest opacity-35">Drop</p>
                <p class="mt-1 font-bold leading-relaxed">{{ manualOrder.delivery_address }}</p>
              </div>
              <div>
                <p class="font-black uppercase tracking-widest opacity-35">Amount</p>
                <p class="mt-1 text-base font-black text-primary">Rs {{ manualOrder.payable_amount | number:'1.0-0' }}</p>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-black uppercase tracking-widest opacity-60">Riders</h4>
              <button class="btn btn-xs btn-outline rounded-lg" (click)="loadRiders()" [disabled]="isAssigningRider()">
                <i class="fa-solid fa-rotate"></i>
                Refresh
              </button>
            </div>

            @if (manualRidersForOrder(manualOrder).length > 0) {
              <div class="space-y-3">
                @for (rider of manualRidersForOrder(manualOrder); track rider.id) {
                  <label class="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-base-200 p-4 transition hover:bg-base-200/40" [class.border-warning]="selectedManualRiderId() === rider.id" [class.bg-warning/5]="selectedManualRiderId() === rider.id">
                    <div class="flex min-w-0 items-center gap-3">
                      <input type="radio" name="manual-rider" class="radio radio-warning radio-sm" [value]="rider.id" [ngModel]="selectedManualRiderId()" (ngModelChange)="selectedManualRiderId.set($event)">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-base-200 text-sm font-black">
                        {{ (rider.full_name || rider.code || 'R')[0] }}
                      </div>
                      <div class="min-w-0">
                        <p class="truncate text-sm font-black">{{ rider.full_name || rider.code || 'Rider' }}</p>
                        <p class="truncate text-[10px] font-bold uppercase tracking-widest opacity-45">
                          {{ rider.vehicle_type || 'Vehicle' }} / {{ rider.presence?.status || rider.status || 'Offline' }}
                        </p>
                      </div>
                    </div>
                    <span class="badge badge-sm border-0 text-[9px] font-black uppercase" [class.badge-success]="rider.presence?.is_online" [class.badge-ghost]="!rider.presence?.is_online">
                      {{ rider.presence?.is_online ? 'Online' : 'Offline' }}
                    </span>
                  </label>
                }
              </div>
            } @else {
              <div class="rounded-2xl border border-dashed border-base-300 bg-base-200/30 p-8 text-center">
                <p class="text-sm font-black opacity-60">No approved riders found for this store</p>
                <p class="mt-2 text-[10px] font-bold uppercase tracking-widest opacity-35">Refresh or check rider verification</p>
              </div>
            }
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 border-t border-base-200 p-5">
          <button class="btn btn-sm btn-ghost rounded-xl" (click)="closeManualAssignment()">Close</button>
          <button class="btn btn-sm btn-warning rounded-xl" (click)="assignManualRider()" [disabled]="!selectedManualRiderId() || isAssigningRider()">
            @if (isAssigningRider()) {
              <span class="loading loading-spinner loading-xs"></span>
            }
            Assign rider
          </button>
        </div>
      </div>
    </div>
  }
</div>
